import { useEffect, useRef } from "react";

type BrowseSwipeOptions = {
  enabled: boolean;
  /** Swipe left → call this (e.g. open För dig) */
  onSwipeLeft?: () => void;
  /** Swipe right → call this (e.g. back to Alla) */
  onSwipeRight?: () => void;
  /** Live progress while dragging: left swipe 0→1, right swipe 0→-1 */
  onProgress?: (progress: number) => void;
  /** Gesture ended without committing */
  onCancel?: () => void;
  /** Min horizontal distance to commit */
  thresholdPx?: number;
  /**
   * Optional: only start the gesture near a screen edge.
   * Omit for full-screen swipe (Alla → För dig).
   */
  edge?: "left" | "right";
  edgePx?: number;
};

function isInsideNoBrowseSwipe(target: EventTarget | null, root: HTMLElement) {
  let node = target as HTMLElement | null;
  while (node && node !== root) {
    if (node.dataset?.noBrowseSwipe != null) return true;
    node = node.parentElement;
  }
  return false;
}

/**
 * Horizontal page swipe between Alla ↔ För dig.
 * Uses Pointer Events so it works with touch AND mouse (desktop mobile viewport).
 */
export function useBrowseModeSwipe(
  targetRef: React.RefObject<HTMLElement | null>,
  {
    enabled,
    onSwipeLeft,
    onSwipeRight,
    onProgress,
    onCancel,
    thresholdPx = 56,
    edge,
    edgePx = 28,
  }: BrowseSwipeOptions,
) {
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const axis = useRef<"h" | "v" | null>(null);
  const armed = useRef(false);
  const pointerId = useRef<number | null>(null);
  const leftRef = useRef(onSwipeLeft);
  const rightRef = useRef(onSwipeRight);
  const progressRef = useRef(onProgress);
  const cancelRef = useRef(onCancel);
  leftRef.current = onSwipeLeft;
  rightRef.current = onSwipeRight;
  progressRef.current = onProgress;
  cancelRef.current = onCancel;

  useEffect(() => {
    if (!enabled) return;

    let el: HTMLElement | null = null;
    let raf = 0;
    let disposed = false;

    const resetGesture = () => {
      startX.current = null;
      startY.current = null;
      axis.current = null;
      armed.current = false;
      pointerId.current = null;
    };

    const onPointerDown = (e: PointerEvent) => {
      // Primary button / touch only
      if (e.pointerType === "mouse" && e.button !== 0) return;
      if (pointerId.current != null) return;

      const x = e.clientX;
      const w = window.innerWidth || 1;
      if (edge === "right" && x < w - edgePx) return;
      if (edge === "left" && x > edgePx) return;
      if (!el || isInsideNoBrowseSwipe(e.target, el)) return;

      pointerId.current = e.pointerId;
      armed.current = true;
      startX.current = x;
      startY.current = e.clientY;
      axis.current = null;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (
        !armed.current ||
        pointerId.current !== e.pointerId ||
        startX.current == null ||
        startY.current == null
      ) {
        return;
      }
      const dx = e.clientX - startX.current;
      const dy = e.clientY - startY.current;
      if (!axis.current) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        axis.current = Math.abs(dx) >= Math.abs(dy) * 1.15 ? "h" : "v";
        if (axis.current === "v") {
          // Let the page scroll — abandon browse swipe
          cancelRef.current?.();
          resetGesture();
          return;
        }
        // Lock horizontal: capture so we keep getting moves
        try {
          el?.setPointerCapture?.(e.pointerId);
        } catch {
          // ignore
        }
      }
      if (axis.current !== "h") return;

      // Avoid text selection / native pan while swiping pages
      e.preventDefault();

      const w = window.innerWidth || 1;
      const raw = -dx / (w * 0.42);
      progressRef.current?.(Math.max(-1, Math.min(1, raw)));
    };

    const finish = (e: PointerEvent) => {
      if (pointerId.current !== e.pointerId) return;
      try {
        el?.releasePointerCapture?.(e.pointerId);
      } catch {
        // ignore
      }

      if (!armed.current || startX.current == null || axis.current !== "h") {
        if (armed.current && axis.current !== "h") {
          // already cancelled on vertical lock
        } else if (armed.current) {
          cancelRef.current?.();
        }
        resetGesture();
        return;
      }

      const dx = e.clientX - startX.current;
      if (dx <= -thresholdPx) leftRef.current?.();
      else if (dx >= thresholdPx) rightRef.current?.();
      else cancelRef.current?.();
      resetGesture();
    };

    const onPointerUp = (e: PointerEvent) => finish(e);
    const onPointerCancel = (e: PointerEvent) => {
      if (pointerId.current !== e.pointerId) return;
      cancelRef.current?.();
      resetGesture();
    };

    const bind = () => {
      if (disposed) return;
      el = targetRef.current;
      if (!el) {
        raf = requestAnimationFrame(bind);
        return;
      }
      el.addEventListener("pointerdown", onPointerDown, { passive: true });
      el.addEventListener("pointermove", onPointerMove, { passive: false });
      el.addEventListener("pointerup", onPointerUp, { passive: true });
      el.addEventListener("pointercancel", onPointerCancel, { passive: true });
      // Help browsers treat this as a pan target
      el.style.touchAction = el.style.touchAction || "pan-y";
    };

    bind();

    return () => {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      if (!el) return;
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerCancel);
    };
  }, [enabled, targetRef, thresholdPx, edge, edgePx]);
}
